import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import KpiCard from "@/components/KpiCard";
import LoadFactorChart from "@/components/LoadFactorChart";
import { MonthlyKwhChart, Co2TrendChart, CostBreakdownChart } from "@/components/MonthlyCharts";
import BillsTable from "@/components/BillsTable";
import AIInsights from "@/components/AIInsights";
import YoYBlock from "@/components/YoYBlock";
import BudgetAlert from "@/components/BudgetAlert";
import TariffCallout from "@/components/TariffCallout";
import CompareWidget from "@/components/CompareWidget";
import PerformanceKPIs from "@/components/PerformanceKPIs";
import YoYCharts from "@/components/YoYCharts";
import { analyticsSummary, seedSample, analyticsCompare } from "@/lib/api";
import { useAccounts } from "@/contexts/AccountContext";
import { DASH } from "@/constants/testIds";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Dashboard() {
  const { selectedId, accounts } = useAccounts();
  const [data, setData] = useState({ months: [], kpis: {}, yoy: null, tariff_spikes: [], budget: null });
  const [compare, setCompare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [insightsKey, setInsightsKey] = useState(0);
  const nav = useNavigate();
  const printRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        analyticsSummary(selectedId),
        analyticsCompare(selectedId).catch(() => null),
      ]);
      setData(d);
      setCompare(c);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedSample();
      toast.success("Sample bills loaded");
      await load();
      setInsightsKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not seed sample data");
    } finally {
      setSeeding(false);
    }
  };

  const handleExport = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(printRef.current, {
        backgroundColor: "#F7F7F4",
        scale: 2,
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW - 48;
      const imgH = (canvas.height * imgW) / canvas.width;
      // Multi-page split
      let y = 24;
      if (imgH < pageH - 48) {
        pdf.addImage(imgData, "JPEG", 24, y, imgW, imgH);
      } else {
        let remainingH = imgH;
        let srcY = 0;
        const perPage = pageH - 48;
        while (remainingH > 0) {
          const sliceH = Math.min(perPage, remainingH);
          const sliceRatio = sliceH / imgH;
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = canvas.height * sliceRatio;
          const ctx = sliceCanvas.getContext("2d");
          ctx.drawImage(
            canvas,
            0, srcY, canvas.width, sliceCanvas.height,
            0, 0, canvas.width, sliceCanvas.height
          );
          const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
          pdf.addImage(sliceData, "JPEG", 24, 24, imgW, sliceH);
          srcY += sliceCanvas.height;
          remainingH -= sliceH;
          if (remainingH > 0) pdf.addPage();
        }
      }
      const stamp = new Date().toISOString().slice(0, 10);
      pdf.save(`kilowatt-ledger-${stamp}.pdf`);
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const { months = [], kpis = {}, yoy, yoy_series, tariff_spikes = [], budget } = data;
  const hasBills = months.length > 0;
  const selectedAcc = accounts.find((a) => a.account_id === selectedId);

  return (
    <Layout>
      <div data-testid={DASH.page} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Masthead */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4"
        >
          <div>
            <div className="overline mb-3">
              {selectedAcc?.name || "Ledger"} · Vol. 01 · {kpis.months_tracked || 0} issues on file
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Your sustainability ledger.
            </h1>
            <p className="mt-4 text-base text-[#6E716C] max-w-xl font-body">
              Every kilowatt-hour, priced, weighed for carbon, and read like a small daily paper.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {hasBills && (
              <button
                data-testid={DASH.exportPdfBtn}
                onClick={handleExport}
                disabled={exporting}
                className="px-5 py-3 border border-[#E5E5DE] hover:border-[#0F3524] transition-colors text-sm font-medium disabled:opacity-60"
              >
                {exporting ? "Exporting…" : "Export PDF"}
              </button>
            )}
            <button
              onClick={() => nav("/upload")}
              className="px-5 py-3 bg-[#0F3524] text-[#F7F7F4] font-medium tracking-tight hover:bg-[#174D35] transition-colors"
            >
              Upload a bill →
            </button>
          </div>
        </motion.div>

        {loading ? (
          <div className="font-mono-data text-sm text-[#6E716C]">Loading ledger…</div>
        ) : !hasBills ? (
          <EmptyState onSeed={handleSeed} seeding={seeding} />
        ) : (
          <div ref={printRef} className="space-y-6">
            {/* Budget alert (if enabled) */}
            {budget?.enabled && <BudgetAlert budget={budget} />}

            {/* Tariff spike callout */}
            {tariff_spikes.length > 0 && <TariffCallout spikes={tariff_spikes} />}

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                testid={DASH.kpiCard("total-kwh")}
                label="Total kWh"
                value={fmt(kpis.total_kwh, 0)}
                unit="kWh"
                delta={`${signed(kpis.delta_kwh_pct)}% MoM`}
                deltaDir={kpis.delta_kwh_pct > 0 ? "up" : kpis.delta_kwh_pct < 0 ? "down" : ""}
                index={0}
              />
              <KpiCard
                testid={DASH.kpiCard("total-cost")}
                label="Total cost"
                value={`RM ${fmt(kpis.total_cost_rm, 2)}`}
                delta={`${signed(kpis.delta_cost_pct)}% MoM`}
                deltaDir={kpis.delta_cost_pct > 0 ? "up" : kpis.delta_cost_pct < 0 ? "down" : ""}
                index={1}
              />
              <KpiCard
                testid={DASH.kpiCard("avg-cpk")}
                label="Avg cost / kWh"
                value={`RM ${fmt(kpis.avg_cost_per_kwh, 4)}`}
                delta={`${signed(kpis.delta_cpk_pct)}% MoM`}
                deltaDir={kpis.delta_cpk_pct > 0 ? "up" : kpis.delta_cpk_pct < 0 ? "down" : ""}
                index={2}
              />
              <KpiCard
                testid={DASH.kpiCard("load-factor")}
                label="Avg load factor"
                value={fmt(kpis.avg_load_factor, 3)}
                delta={`CO₂ ${fmt(kpis.total_co2_kg, 0)} kg`}
                deltaDir=""
                index={3}
              />
            </div>

            {/* Featured chart */}
            <LoadFactorChart months={months} spikes={tariff_spikes} />

            {/* Secondary charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <MonthlyKwhChart months={months} />
              <Co2TrendChart months={months} />
              <CostBreakdownChart months={months} />
            </div>

            {/* Year over Year (numeric MoM/YoY block) */}
            <YoYBlock yoy={yoy} />

            {/* Performance & KPIs (new expanded section) */}
            <PerformanceKPIs months={months} kpis={kpis} />

            {/* Year-over-Year 4-chart grid */}
            <YoYCharts series={yoy_series} yoy={yoy} months={months} />

            {/* How you compare (anonymized peer benchmark) */}
            <CompareWidget compare={compare} />

            {/* AI Insights */}
            <AIInsights hasBills={hasBills} refreshKey={insightsKey} />

            {/* Bills Table */}
            <BillsTable months={months} onChange={load} />
          </div>
        )}
      </div>
    </Layout>
  );
}

function fmt(n, digits = 2) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-MY", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
function signed(n) {
  if (typeof n !== "number") return "0.00";
  return n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2);
}

function EmptyState({ onSeed, seeding }) {
  return (
    <div
      data-testid={DASH.emptyState}
      className="flat-card p-10 sm:p-16 text-center"
    >
      <div className="overline mb-4">Empty ledger</div>
      <h2 className="font-display text-3xl sm:text-4xl tracking-tight max-w-xl mx-auto">
        Every archive starts with the first entry.
      </h2>
      <p className="text-[#6E716C] mt-4 max-w-lg mx-auto">
        Upload a TNB bill (PDF) to begin tracking — or load 5 pre-parsed sample months to explore the dashboard.
      </p>
      <div className="mt-8 flex items-center justify-center gap-3">
        <button
          data-testid={DASH.loadSampleBtn}
          onClick={onSeed}
          disabled={seeding}
          className="px-6 py-3 bg-[#0F3524] text-[#F7F7F4] font-medium tracking-tight hover:bg-[#174D35] transition-colors disabled:opacity-60"
        >
          {seeding ? "Loading sample…" : "Load 5 sample months"}
        </button>
        <a
          href="/upload"
          className="px-6 py-3 border border-[#E5E5DE] hover:border-[#0F3524] transition-colors font-medium"
        >
          Upload your own PDF
        </a>
      </div>
    </div>
  );
}
